import { useEffect, useState } from "react";
import { useMutation, useQuery, useSubscription } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { Button } from "@/components/design/Button";
import {
  ActOnNotificationDocument,
  CockpitNotificationsDocument,
  DeleteNotificationDocument,
  MarkNotificationReadDocument,
  NotificationEventsDocument,
  UnreadNotificationCountUpdatesDocument,
  type CockpitNotificationsQuery,
} from "@/graphql/generated/operations";
import { logApplicationFailure } from "@/lib/user-error";
import { ALL_NOTIFICATION_TYPES } from "@/lib/notification-types";

type Notification = CockpitNotificationsQuery["notifications"][number];

// Topbar notification inbox: bell + unread badge + dropdown list. The list and
// the badge update live via the notificationEvents / unreadNotificationCountUpdates
// subscriptions (BFF injects the bearer on connection_init). Each event refetches
// the inbox query so the list stays authoritative; the count subscription keeps
// the badge instant. Actions (mark read, dismiss, accept/reject contact requests)
// route through the existing notification mutations.
export function NotificationCenter() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data, refetch } = useQuery(CockpitNotificationsDocument, {
    variables: {
      input: { take: 20, supportedTypes: ALL_NOTIFICATION_TYPES },
      supported: { supportedTypes: ALL_NOTIFICATION_TYPES },
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: eventData } = useSubscription(NotificationEventsDocument, {
    variables: { supported: { supportedTypes: ALL_NOTIFICATION_TYPES } },
  });
  const { data: countData } = useSubscription(UnreadNotificationCountUpdatesDocument, {
    variables: { supported: { supportedTypes: ALL_NOTIFICATION_TYPES } },
  });

  const [markRead] = useMutation(MarkNotificationReadDocument);
  const [dismiss] = useMutation(DeleteNotificationDocument);
  const [actOn] = useMutation(ActOnNotificationDocument);

  // The query is the single source of truth for the list and the badge count;
  // both subscriptions just refetch it so it stays authoritative without local
  // state syncing. unreadNotificationCount rides along on the same query.
  useEffect(() => {
    if (eventData?.notificationEvents) void refetch();
  }, [eventData, refetch]);

  useEffect(() => {
    if (countData?.unreadNotificationCountUpdates) void refetch();
  }, [countData, refetch]);

  const count = data?.unreadNotificationCount ?? 0;

  const notifications = (data?.notifications ?? []).filter(
    (n) => n.state === "ACTIVE" || n.state === "ACTIONED",
  );

  async function onMarkRead(n: Notification) {
    if (n.readAt) return;
    try {
      await markRead({ variables: { notificationId: n.id } });
      await refetch();
    } catch {
      logApplicationFailure("notifications.mark-read");
    }
  }

  async function onDismiss(n: Notification) {
    try {
      await dismiss({ variables: { notificationId: n.id } });
      await refetch();
    } catch {
      logApplicationFailure("notifications.dismiss");
    }
  }

  async function onAct(n: Notification, action: NonNullable<Notification["actions"]>[number]) {
    try {
      await actOn({ variables: { input: { notificationId: n.id, action: action.type } } });
      await refetch();
    } catch {
      logApplicationFailure("notifications.action");
    }
  }

  function onOpenNotification(n: Notification) {
    void onMarkRead(n);
    if (n.deepLink) {
      setOpen(false);
      navigate(n.deepLink);
    }
  }

  const label = intl.formatMessage({ id: "cockpit.notifications", defaultMessage: "Notifications" });

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full text-ink/70 transition hover:bg-ink/[0.06] hover:text-ink ${
          open ? "bg-ink/[0.06] text-ink ring-1 ring-ink/15" : ""
        }`}
      >
        <BellIcon />
        {count > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-medium leading-[18px] text-on-accent">
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <div aria-hidden="true" onClick={() => setOpen(false)} className="fixed inset-0 z-30" />
          <div
            role="menu"
            className="absolute right-0 top-full z-40 mt-2 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md bg-surface shadow-lg ring-1 ring-ink/10"
          >
            <div className="flex items-center justify-between gap-2 border-b border-ink/5 px-4 py-3">
              <div className="text-[10px] tracking-[0.25em] text-ink/55">{label}</div>
              {count > 0 ? (
                <span className="text-[11px] tabular-nums text-ink/40">
                  {count} <FormattedMessage id="cockpit.unread" defaultMessage="unread" />
                </span>
              ) : null}
            </div>

            <div className="max-h-[70vh] divide-y divide-ink/5 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-[12px] text-ink/45">
                  <FormattedMessage id="cockpit.noNotifications" defaultMessage="No notifications" />
                </div>
              ) : (
                notifications.map((n) => (
                  <NotificationRow
                    key={n.id}
                    n={n}
                    when={n.createdAt ? intl.formatDate(n.createdAt, { dateStyle: "short", timeStyle: "short" }) : ""}
                    markReadLabel={intl.formatMessage({ id: "cockpit.markRead", defaultMessage: "Mark read" })}
                    dismissLabel={intl.formatMessage({ id: "cockpit.dismiss", defaultMessage: "Dismiss" })}
                    onOpen={() => onOpenNotification(n)}
                    onMarkRead={() => void onMarkRead(n)}
                    onDismiss={() => void onDismiss(n)}
                    onAct={(action) => void onAct(n, action)}
                  />
                ))
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function NotificationRow({
  n,
  when,
  markReadLabel,
  dismissLabel,
  onOpen,
  onMarkRead,
  onDismiss,
  onAct,
}: {
  n: Notification;
  when: string;
  markReadLabel: string;
  dismissLabel: string;
  onOpen: () => void;
  onMarkRead: () => void;
  onDismiss: () => void;
  onAct: (action: NonNullable<Notification["actions"]>[number]) => void;
}) {
  const unread = !n.readAt;
  const availableActions = (n.actions ?? []).filter((a) => a.status === "AVAILABLE");

  return (
    <div className={`flex gap-2 px-4 py-3 ${unread ? "bg-accent/[0.04]" : ""}`}>
      <span
        aria-hidden="true"
        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${unread ? "bg-accent" : "bg-transparent"}`}
      />
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onOpen}
          className="block w-full text-left"
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[13px] font-medium text-ink">{n.title?.localized || "–"}</span>
            <span className="shrink-0 text-[10px] text-ink/40">{when}</span>
          </div>
          {n.body?.localized ? (
            <p className="mt-0.5 line-clamp-3 text-[12px] leading-relaxed text-ink/55">{n.body.localized}</p>
          ) : null}
        </button>

        {availableActions.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {availableActions.map((a) => (
              <Button
                key={a.type}
                variant="secondary"
                size="sm"
                onClick={() => onAct(a)}
              >
                {a.label?.localized || a.type}
              </Button>
            ))}
          </div>
        ) : null}

        <div className="mt-1.5 flex items-center gap-3">
          {unread ? (
            <button
              type="button"
              onClick={onMarkRead}
              className="text-[10px] tracking-[0.12em] text-ink/45 transition hover:text-ink"
            >
              {markReadLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDismiss}
            className="text-[10px] tracking-[0.12em] text-ink/45 transition hover:text-red-600"
          >
            {dismissLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function BellIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
      />
    </svg>
  );
}
